import { useState, useEffect } from "react";

import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { useNavigate } from "react-router-dom";

import moneyAnimation from "../assets/Money.json";
import redGiftAnimation from "../assets/red_gift.json";
import rewardsAnimation from "../assets/rewards.json";

import ResellerManagedBadge from "./ResellerManagedBadge";

const DynamicCTABanner = ({
  isPremium,
  isReseller,
  onAddPlaylist,
  onBuyPremium,
  user,
  totalUsers = 0,
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();

  // Module resellers don't follow the 25-user minimum / tier ladder, so they
  // never see the low-user warnings.
  const isModuleReseller = isReseller && user?.resellerType === "module";

  // Get color for total users count based on thresholds
  const getUserCountColor = () => {
    if (isModuleReseller) return "text-gray-900 dark:text-white";
    if (totalUsers <= 25) return "text-red-500 dark:text-red-400";
    if (totalUsers > 25 && totalUsers <= 30)
      return "text-orange-500 dark:text-orange-400";

    return "text-gray-900 dark:text-white";
  };

  // Check if banner should be shown based on user preference
  useEffect(() => {
    // For free users, always show the banner (no option to hide)
    if (!isPremium) {
      setIsVisible(true);

      return;
    }

    // For premium users, check preference
    if (user?.preferences?.showWelcomeBanner === false) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
  }, [isPremium, user?.preferences?.showWelcomeBanner]);

  // Rotating messages for free tier users
  const freeMessages = [
    "Buy a Redeem code to unlock all premium features.",
    "Join thousands of users enjoying full access with Lunova Player Premium.",
    "Get instant access to advanced tools and a seamless experience.",
    "Premium members enjoy unlimited playlists and device management.",
    "Upgrade your account today.",
    "Access exclusive app features designed for power users.",
    "Take control of your viewing with custom playlists, profiles, and more.",
  ];

  // Rotating messages for premium users
  const premiumMessages = [
    "Enjoy unlimited playlists and seamless device management.",
    "Organize playlists and devices your way.",
    "Download Lunova Player on all your devices.",
    "Customize your player with profiles and settings.",
    "Switch effortlessly between TV, tablet, and phone.",
    "Keep all your preferences synced across devices.",
    "Personalize your experience with advanced options.",
    "Manage profiles for family, friends, or guests.",
    "Quickly access your most used features in one place.",
    "Your premium unlocks the full power of Lunova Player.",
  ];

  // Rotate messages every 3 seconds (only for non-reseller users)
  useEffect(() => {
    if (isReseller) return; // Don't rotate for resellers

    const messages = isPremium ? premiumMessages : freeMessages;
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isPremium, isReseller]);

  // Determine which CTA to show based on user state
  const getCTAContent = () => {
    // Reseller users - show reseller dashboard info
    if (isReseller) {
      return {
        useLottie: true,
        lottieAnimation: moneyAnimation,
        title: "Reseller Dashboard",
        subtitle: "Grow your business with Lunova Player",
        isReseller: true,
        buttons: [],
      };
    }

    // Free tier users (no premium) - prioritize monetization
    if (!isPremium) {
      return {
        useLottie: true,
        lottieAnimation: redGiftAnimation,
        title: "Unlock Premium Streaming",
        subtitle: freeMessages[messageIndex],
        buttons: [
          { label: "Buy Premium", onClick: onBuyPremium, primary: true },
        ],
      };
    }

    // Premium users - show thank you message with rotating benefits
    return {
      useLottie: true,
      lottieAnimation: rewardsAnimation,
      title: "Thank you for being a Premium member!",
      subtitle: premiumMessages[messageIndex],
      buttons: [
        { label: "+ Add Playlist", onClick: onAddPlaylist, primary: true },
      ],
    };
  };

  const content = getCTAContent();

  // Don't render if not visible
  if (!isVisible) return null;

  return (
    <div
      className={`card p-6 shadow-lg rounded-xl overflow-hidden relative ${
        isReseller
          ? "bg-white dark:bg-gray-800"
          : "bg-gradient-to-r from-purple-600 to-indigo-600 text-white"
      }`}
    >
      <div className="flex flex-col gap-2">
        {/* Add padding when reseller to avoid overlap with minimized tier widget */}
        <div
          className={`flex flex-col md:flex-row items-center justify-between gap-4 relative ${isReseller ? "pr-4" : ""}`}
        >
          {/* Total Users Badge for Resellers - Aligned with header */}
          {isReseller && (
            <div className="absolute right-4 top-0 flex flex-col items-center gap-1.5 bg-gray-50 dark:bg-gray-700/50 backdrop-blur-sm px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 shadow-md z-10">
              <span className="text-xs text-gray-700 dark:text-gray-300 uppercase tracking-wide font-semibold">
                Total Users
              </span>
              <span
                className={`font-bold ${getUserCountColor()}`}
                style={{ fontSize: "36px", lineHeight: "1" }}
              >
                {totalUsers}
              </span>
              {totalUsers < 25 && !isModuleReseller && (
                <>
                  <button
                    className="mt-1 px-4 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-md transition-all transform hover:scale-105 shadow-md"
                    onClick={onAddPlaylist}
                  >
                    + Add More
                  </button>
                  <div className="mt-1 text-center">
                    <p className="text-[10px] text-yellow-700 dark:text-yellow-300 font-medium leading-tight max-w-[120px]">
                      ⚠️ Keep 25+ users to avoid suspension
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex items-center gap-4">
            {content.useLottie ? (
              <div className="w-36 h-36 flex items-center justify-center">
                <Lottie
                  animationData={content.lottieAnimation}
                  autoplay={true}
                  loop={false}
                  style={{ width: 144, height: 144 }}
                />
              </div>
            ) : (
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl text-2xl backdrop-blur-sm shadow-inner">
                {content.icon}
              </div>
            )}
            <div className="flex-1">
              {isPremium && !isReseller && user?.resellerManaged && (
                <div className="mb-2">
                  <ResellerManagedBadge />
                </div>
              )}
              <h2
                className={`text-2xl font-bold mb-1 ${isReseller ? "text-gray-900 dark:text-white" : "text-white"}`}
              >
                {content.title}
              </h2>
              {content.isReseller ? (
                <p className="text-gray-700 dark:text-white text-sm md:text-base">
                  {content.subtitle}
                </p>
              ) : (
                <AnimatePresence mode="wait">
                  <motion.p
                    animate={{ opacity: 1, y: 0 }}
                    className={`${isReseller ? "text-gray-700 dark:text-white" : "text-purple-100"} text-sm md:text-base`}
                    exit={{ opacity: 0, y: -10 }}
                    initial={{ opacity: 0, y: 10 }}
                    key={messageIndex}
                    transition={{ duration: 0.3 }}
                  >
                    {content.subtitle}
                  </motion.p>
                </AnimatePresence>
              )}
            </div>
          </div>

          <div className="flex gap-3 flex-wrap justify-center md:justify-end">
            {content.buttons.map((button, index) => (
              <button
                className={`px-5 py-2.5 rounded-lg font-semibold transition-all transform hover:scale-105 ${
                  button.primary
                    ? isReseller
                      ? "bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
                      : "bg-white text-purple-600 hover:bg-purple-50 shadow-lg"
                    : isReseller
                      ? "bg-gray-700/50 text-white hover:bg-gray-700 backdrop-blur-sm"
                      : "bg-purple-700/50 text-white hover:bg-purple-700 backdrop-blur-sm"
                }`}
                key={index}
                onClick={button.onClick}
              >
                {button.label}
              </button>
            ))}
          </div>
        </div>
        {/* Subtext for premium users */}
        {isPremium && !isReseller && (
          <div className="text-center">
            <p className="text-xs text-purple-200 opacity-80">
              You can turn off this banner in your{" "}
              <button
                className="underline hover:text-white transition-colors"
                onClick={() => navigate("/profile?tab=preferences")}
              >
                account preferences
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicCTABanner;
